import { ItemMenu, MENU } from '@/core/components/database/menu';
import { ArmazenamentoService } from '@/core/services/armazenamento/armazenamento.service';
import type { IconeNome } from '@/shared/components/ui/icon';
import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

export type Layout = 'vertical' | 'horizontal';

const LAYOUT_PADRAO: Layout = 'horizontal';
const MOBILE = '(max-width: 768px)';

const ROTAS_MENU: readonly string[] = MENU.flatMap((item) => [
  ...(item.rota ? [item.rota] : []),
  ...(item.submenu ?? []).map((sub) => sub.rota),
]);

export interface PaginaNavegacao {
  rota: string;
  rotulo: string;
  icone: IconeNome;
  secao: string | null;
}

const ROTA_DASHBOARD = '/dashboard';
const PAGINA_AGENDA: PaginaNavegacao = { rota: '/agenda', rotulo: 'Agenda', icone: 'calendario', secao: null };

/**
 * Estado da navegação do shell (layout, sidebar, subnav, drawer mobile e painel de configurações).
 *
 * As preferências são salvas por usuário no armazenamento. Chamar `carregar` quando o usuário logado for conhecido.
 */
@Injectable({ providedIn: 'root' })
export class NavegacaoService {
  private readonly armazenamento = inject(ArmazenamentoService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private idUsuario: string | number | null = null;

  private readonly _layout = signal<Layout>(LAYOUT_PADRAO);
  private readonly _sidenavRecolhida = signal(false);
  private readonly _subnavVisivel = signal(true);
  private readonly _drawerAberto = signal(false);
  private readonly _configAberta = signal(false);
  private readonly _mobile = signal(false);
  private readonly _rotaAtiva = signal<string | null>(null);
  private readonly email = signal<string | null>(null);

  /** Layout escolhido pelo usuário (no mobile o efetivo é sempre vertical). */
  readonly layout = this._layout.asReadonly();
  readonly sidenavRecolhida = this._sidenavRecolhida.asReadonly();
  readonly subnavVisivel = this._subnavVisivel.asReadonly();
  readonly drawerAberto = this._drawerAberto.asReadonly();
  readonly configAberta = this._configAberta.asReadonly();
  readonly mobile = this._mobile.asReadonly();
  readonly rotaAtiva = this._rotaAtiva.asReadonly();

  readonly layoutEfetivo = computed<Layout>(() => (this._mobile() ? 'vertical' : this._layout()));

  readonly menu = computed<ItemMenu[]>(() => {
    const email = this.email();
    const visivel = (logins?: string[]) => !logins?.length || (!!email && logins.includes(email));

    return MENU.flatMap((item) => {
      if (!visivel(item.logins)) return [];
      if (!item.submenu) return [item];

      const submenu = item.submenu.filter((sub) => visivel(sub.logins));
      return submenu.length ? [{ ...item, submenu }] : [];
    });
  });

  readonly paginas = computed<PaginaNavegacao[]>(() => {
    const paginas: PaginaNavegacao[] = [];
    for (const item of this.menu()) {
      if (item.submenu) {
        for (const sub of item.submenu) {
          paginas.push({ rota: sub.rota, rotulo: sub.rotulo, icone: sub.icone, secao: item.rotulo });
        }
      } else if (item.rota && item.rota !== ROTA_DASHBOARD) {
        paginas.push({ rota: item.rota, rotulo: item.rotulo, icone: item.icone, secao: null });
      }
    }
    return [PAGINA_AGENDA, ...paginas];
  });

  constructor() {
    this.observarMobile();
    this.atualizarRotaAtiva();

    this.router.events
      .pipe(
        filter((evento) => evento instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.atualizarRotaAtiva();
        this.fecharDrawer();
      });

    effect(
      () => {
        if (!this._mobile()) this._drawerAberto.set(false);
      },
      { allowSignalWrites: true }
    );
  }

  carregar(idUsuario: string | number, email: string): void {
    this.idUsuario = idUsuario;
    this.email.set(email);

    if (!this.isBrowser) return;

    localStorage.removeItem(`viewTopnav_${idUsuario}`);
    sessionStorage.removeItem(`viewTopnav_${idUsuario}`);

    const layout = this.armazenamento.obterItem(`layout_navigation_${idUsuario}`);
    this._layout.set(layout === 'vertical' || layout === 'horizontal' ? layout : LAYOUT_PADRAO);

    const sidenav = this.armazenamento.obterItem(`viewSidenav_${idUsuario}`);
    this._sidenavRecolhida.set(Number(sidenav) === 1);

    const subnav = this.armazenamento.obterItem(`navSubnav_${idUsuario}`);
    this._subnavVisivel.set(subnav !== '0');
  }

  definirLayout(layout: Layout): void {
    this._layout.set(layout);
    this.gravar('layout_navigation', layout);
  }

  definirSidenavRecolhida(recolhida: boolean): void {
    this._sidenavRecolhida.set(recolhida);
    this.gravar('viewSidenav', recolhida ? '1' : '0');
  }

  alternarSidenav(): void {
    this.definirSidenavRecolhida(!this._sidenavRecolhida());
  }

  /** Salvar `false` só muda o estado na tela (usado pelo botão de menu da topbar). */
  definirSubnavVisivel(visivel: boolean, salvar = true): void {
    this._subnavVisivel.set(visivel);
    if (salvar) this.gravar('navSubnav', visivel ? '1' : '0');
  }

  alternarSubnav(): void {
    this.definirSubnavVisivel(!this._subnavVisivel(), false);
  }

  alternarDrawer(): void {
    this._drawerAberto.update((aberto) => !aberto);
  }

  fecharDrawer(): void {
    this._drawerAberto.set(false);
  }

  alternarConfig(): void {
    this._configAberta.update((aberta) => !aberta);
  }

  fecharConfig(): void {
    this._configAberta.set(false);
  }

  private gravar(prefixo: string, valor: string): void {
    if (this.idUsuario === null) return;
    this.armazenamento.definirItem(`${prefixo}_${this.idUsuario}`, valor);
  }

  /** Rota do menu mais longa que é prefixo da URL atual (sem query string nem fragmento). */
  private atualizarRotaAtiva(): void {
    const url = this.router.url.split('?')[0].split('#')[0];
    let melhor: string | null = null;

    for (const rota of ROTAS_MENU) {
      const casa = url === rota || url.startsWith(rota + '/');
      if (casa && (melhor === null || rota.length > melhor.length)) melhor = rota;
    }

    this._rotaAtiva.set(melhor);
  }

  private observarMobile(): void {
    if (!this.isBrowser) return;

    const mq = window.matchMedia(MOBILE);
    this._mobile.set(mq.matches);

    const aoMudar = (evento: MediaQueryListEvent) => this._mobile.set(evento.matches);
    mq.addEventListener('change', aoMudar);
    this.destroyRef.onDestroy(() => mq.removeEventListener('change', aoMudar));
  }
}
